import { Checkbox, Stack, Text } from "@assetwatch/ui";
import { useState } from "react";
import type { ProcedureDetail } from "../../../queries/useProceduresQuery";
import { ProcedureItemTypeIcon } from "./ProcedureItemTypeIcon";

type Preview = Extract<
  ProcedureDetail["canonicalPreview"],
  { status: "available" }
>;
type PreviewItem =
  Preview["groups"][number]["sections"][number]["items"][number];

export function countProcedurePreviewSteps(preview: Preview) {
  return preview.groups.reduce(
    (groupTotal, group) =>
      groupTotal +
      group.sections.reduce(
        (sectionTotal, section) => sectionTotal + section.items.length,
        0,
      ),
    0,
  );
}

function ResponseField({ item }: { item: PreviewItem }) {
  const label = `${item.label} response`;
  const inputClassName =
    "w-full rounded-(--radius-sm) border border-input-default-outline bg-input-default-background p-(--spacing-8)";

  if (item.type === "text") {
    return (
      <input
        aria-label={label}
        className={`${inputClassName} max-w-[420px]`}
        placeholder="Free text response"
        disabled
      />
    );
  }
  if (item.type === "number") {
    return (
      <div className="flex items-center gap-(--spacing-8)">
        <input
          aria-label={label}
          className={`${inputClassName} max-w-[140px]`}
          placeholder="0"
          type="number"
          disabled
        />
        {item.unit ? (
          <Text size="sm" tone="subtle">
            {item.unit}
          </Text>
        ) : null}
      </div>
    );
  }
  if (item.type === "photo") {
    return (
      <div className="rounded-(--radius-sm) border border-dashed border-outline-subtle p-(--spacing-12) text-text-subtle">
        Attach photo or file
      </div>
    );
  }
  if (item.type === "checkbox") return <Checkbox label="Confirmed" disabled />;

  return (
    <Stack gap={8}>
      {item.options.map((option) => (
        <label
          className="flex items-center gap-(--spacing-8) text-text-default"
          key={option}
        >
          <input aria-label={option} name={item.id} type="radio" disabled />
          {option}
        </label>
      ))}
    </Stack>
  );
}

function PreviewItem({
  allItems,
  depth = 0,
  item,
}: {
  allItems: PreviewItem[];
  depth?: number;
  item: PreviewItem;
}) {
  const children = allItems.filter(
    (candidate) => candidate.parentItemId === item.id,
  );
  return (
    <div className={depth > 0 ? "ml-(--spacing-24)" : undefined}>
      <div className="flex items-start gap-(--spacing-8)">
        <span className="mt-(--spacing-4) shrink-0 text-text-subtle">
          <ProcedureItemTypeIcon type={item.type} size="sm" />
        </span>
        <div className="min-w-0 flex-1">
          <Text>
            {item.label}
            {item.required ? " *" : ""}
          </Text>
          {item.description ? (
            <Text size="sm" tone="subtle">
              {item.description}
            </Text>
          ) : null}
          <div className="mt-(--spacing-8)">
            <ResponseField item={item} />
          </div>
        </div>
      </div>
      {children.length > 0 ? (
        <Stack gap={16}>
          {children.map((child) => (
            <PreviewItem
              allItems={allItems}
              depth={depth + 1}
              item={child}
              key={child.id}
            />
          ))}
        </Stack>
      ) : null}
    </div>
  );
}

export function ProcedureDetailPreview({ preview }: { preview: Preview }) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggle = (id: string) =>
    setCollapsed((current) => ({ ...current, [id]: !current[id] }));
  const renderSections = (group: Preview["groups"][number]) => {
    const sectionIds = new Set(group.sections.map((section) => section.id));
    const rootSections = group.sections.filter(
      (section) =>
        section.parentSectionId === group.id ||
        section.parentSectionId === null ||
        !sectionIds.has(section.parentSectionId),
    );

    const renderSection = (
      section: Preview["groups"][number]["sections"][number],
    ) => {
      const rootItems = section.items.filter(
        (item) => item.parentItemId === null,
      );
      const childSections = group.sections.filter(
        (candidate) => candidate.parentSectionId === section.id,
      );
      return (
        <Stack gap={8} key={section.id}>
          <button
            aria-expanded={!collapsed[section.id]}
            className="flex items-center gap-(--spacing-6) text-left text-text-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-button-focus-ring"
            onClick={() => toggle(section.id)}
            type="button"
          >
            <span aria-hidden="true">{collapsed[section.id] ? "›" : "⌄"}</span>
            {section.title}
          </button>
          {!collapsed[section.id] ? (
            <Stack gap={16}>
              {rootItems.map((item) => (
                <PreviewItem
                  allItems={section.items}
                  item={item}
                  key={item.id}
                />
              ))}
              {childSections.map(renderSection)}
            </Stack>
          ) : null}
        </Stack>
      );
    };

    return rootSections.map(renderSection);
  };

  return (
    <Stack gap={20}>
      {preview.groups.map((group) => {
        const isSyntheticStepsGroup =
          group.title.trim().toLowerCase() === "steps";
        if (isSyntheticStepsGroup) return renderSections(group);
        return (
          <Stack gap={12} key={group.id}>
            <button
              aria-expanded={!collapsed[group.id]}
              className="flex items-center gap-(--spacing-8) border-b border-outline-subtle pb-(--spacing-10) text-left text-text-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-button-focus-ring"
              onClick={() => toggle(group.id)}
              type="button"
            >
              <span aria-hidden="true" className="text-text-subtle">
                {collapsed[group.id] ? "›" : "⌄"}
              </span>
              <Text weight="semibold">{group.title}</Text>
            </button>
            {!collapsed[group.id] ? renderSections(group) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}
